import type { StayShowFilter } from "../schedule/scheduleModel";

export type ActivityTargetType = "pet" | "pack" | "all_packs" | "general";
export type ActivityFrequency = "daily" | "weekly" | "every_n_days" | "monthly";
/** "all" = aparece em qualquer filtro (matilha/geral, ou pet sem reserva no dia). */
export type ActivityNature = "daycare" | "hotel" | "both" | "all";

export function isActivityVisibleForCheckedInPets(
  targetType: ActivityTargetType,
  targetPetId: string | null,
  checkedInPetIds: ReadonlySet<string>,
): boolean {
  return targetType !== "pet" || (targetPetId !== null && checkedInPetIds.has(targetPetId));
}

/** Regra das superfícies de um único pet; a atividade geral pertence só à visão geral. */
export function isActivityVisibleForPet(
  targetType: ActivityTargetType,
  targetPetId: string | null,
  petId: string,
): boolean {
  if (targetType === "general") return false;
  return targetType !== "pet" || targetPetId === petId;
}

export interface ActivityRecurrence {
  frequency: ActivityFrequency;
  intervalDays: number | null;
  daysOfWeek: number[];
  daysOfMonth: number[];
  endsOn: string | null;
}

/** Linha crua de `stay_activity_recurrence`, como vem do select. */
export interface RecurrenceRow {
  frequency: string;
  interval_days: number | null;
  days_of_week: number[] | null;
  days_of_month: number[] | null;
  ends_on: string | null;
}

/** Uma atividade tem no máximo uma recorrência; o select devolve array. */
export function parseActivityRecurrence(rows: RecurrenceRow[] | null | undefined): ActivityRecurrence | null {
  const row = rows?.[0];
  if (!row) return null;
  return {
    frequency: row.frequency as ActivityRecurrence["frequency"],
    intervalDays: row.interval_days,
    daysOfWeek: row.days_of_week ?? [],
    daysOfMonth: row.days_of_month ?? [],
    endsOn: row.ends_on,
  };
}

export interface StayActivityRecord {
  id: string;
  targetType: ActivityTargetType;
  targetPetId: string | null;
  targetPackId: string | null;
  scheduledDate: string | null;
  scheduledTime: string | null;
  description: string | null;
  assignedTo: string | null;
  typeId: string;
  recurrence: ActivityRecurrence | null;
}

/** petId → naturezas das reservas que cobrem o dia (ex.: ["daycare"], ["hotel","daycare"]). */
export type ReservationNatureMap = Record<string, Array<"daycare" | "hotel" | "both">>;

const parse = (iso: string) => new Date(`${iso}T12:00:00`);
const DAY_MS = 24 * 60 * 60 * 1000;

export function diffInDays(fromIso: string, toIso: string): number {
  return Math.round((parse(toIso).getTime() - parse(fromIso).getTime()) / DAY_MS);
}

export function activityOccursOn(
  scheduledDate: string | null,
  recurrence: ActivityRecurrence | null,
  date: string,
): boolean {
  if (!scheduledDate) return false;
  if (date < scheduledDate) return false;
  if (recurrence?.endsOn && date > recurrence.endsOn) return false;
  if (!recurrence) return date === scheduledDate;
  const day = parse(date);
  switch (recurrence.frequency) {
    case "daily":
      return true;
    case "weekly":
      return recurrence.daysOfWeek.includes(day.getDay());
    case "every_n_days": {
      const step = recurrence.intervalDays && recurrence.intervalDays >= 1 ? recurrence.intervalDays : 1;
      return diffInDays(scheduledDate, date) % step === 0;
    }
    case "monthly":
      return recurrence.daysOfMonth.includes(day.getDate());
    default:
      return false;
  }
}

export function deriveActivityNature(
  record: StayActivityRecord,
  naturesByPet: ReservationNatureMap,
  _date: string,
): ActivityNature {
  if (record.targetType !== "pet" || !record.targetPetId) return "all";
  const natures = naturesByPet[record.targetPetId];
  if (!natures || natures.length === 0) return "all";
  if (natures.includes("both")) return "both";
  const hasDaycare = natures.includes("daycare");
  const hasHotel = natures.includes("hotel");
  if (hasDaycare && hasHotel) return "both";
  return hasHotel ? "hotel" : "daycare";
}

export function activityMatchesShowFilter(nature: ActivityNature, show: StayShowFilter): boolean {
  if (show === "all") return true;
  if (nature === "all" || nature === "both") return true;
  return nature === show;
}

/** Próximas `count` datas (iso) a partir da âncora, usando a mesma regra da tabela. */
export function nextOccurrences(scheduledDate: string, recurrence: ActivityRecurrence | null, count: number): string[] {
  const out: string[] = [];
  const start = parse(scheduledDate);
  for (let i = 0; out.length < count && i < 366; i++) {
    const d = new Date(start.getTime() + i * DAY_MS);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (activityOccursOn(scheduledDate, recurrence, iso)) out.push(iso);
  }
  return out;
}

const WEEKDAY_SHORT_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const WEEKDAY_SHORT_EN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function formatRecurrenceLabel(recurrence: ActivityRecurrence | null, locale: string): string {
  const pt = locale.startsWith("pt");
  if (!recurrence) return "";
  const names = pt ? WEEKDAY_SHORT_PT : WEEKDAY_SHORT_EN;
  switch (recurrence.frequency) {
    case "daily":
      return pt ? "Todo dia" : "Every day";
    case "weekly":
      return recurrence.daysOfWeek.length
        ? recurrence.daysOfWeek.slice().sort((a, b) => a - b).map((d) => names[d]).join(" · ")
        : (pt ? "Semanal" : "Weekly");
    case "every_n_days": {
      const n = recurrence.intervalDays ?? 2;
      return pt ? `A cada ${n} dias` : `Every ${n} days`;
    }
    case "monthly":
      return recurrence.daysOfMonth.length
        ? (pt ? `Dia ${recurrence.daysOfMonth.join(", ")}` : `Day ${recurrence.daysOfMonth.join(", ")}`)
        : (pt ? "Mensal" : "Monthly");
    default:
      return "";
  }
}

/** Constrói o mapa petId→naturezas a partir das reservas que cobrem o dia.
 *  Cobertura inline (não usa `coversDate`, que exige um `ScheduleReservation`
 *  completo): a reserva cobre `date` se check-in ≤ date ≤ (check-out ?? check-in). */
export function buildNatureMap(
  reservations: Array<{ petId: string; nature: "daycare" | "hotel" | "both"; checkInDate: string; checkOutDate: string | null }>,
  date: string,
): ReservationNatureMap {
  const map: ReservationNatureMap = {};
  for (const r of reservations) {
    const end = r.checkOutDate ?? r.checkInDate;
    if (!(r.checkInDate <= date && date <= end)) continue;
    (map[r.petId] ??= []).push(r.nature);
  }
  return map;
}
